import * as THREE from "three";
import type RAPIER from "@dimforge/rapier3d-compat";
import { ENCLOSURE_INTERIOR_HEIGHT, isChutePiece, parseArenaDefinition, type ArenaDefinition, type ArenaPieceDefinition, type Vec3 } from "./ArenaDefinition.js";

/**
 * Definition-driven simulation colliders for the arena (LOCKED-DESIGN 18.8).
 *
 * This module is the ONLY place arena colliders are constructed. Its inputs are
 * ArenaDefinition data — piece dims, positions, rotations, and normals — never
 * mesh objects or visual-layout locals, so art-driven edits to the visual
 * builder cannot mutate the deterministic simulation. Geometry shared between
 * a collider and its matching visual lives here as named constants; the visual
 * builder (GrayBoxArena) imports from this module, never the other way around.
 */

/** Chute shell thicknesses consumed by both the chute meshes and the chute colliders. */
export const CHUTE_SHELL = { sideWidth: .24, topHeight: .24, backDepth: .16 } as const;
/** Ramp support slab framing consumed by both the support mesh and its collider. */
export const RAMP_SUPPORT = { margin: .2, depth: .24, normalOffset: -.13 } as const;
/**
 * Placement of the tall enclosure shell relative to the authored floor edge,
 * shared by the shell MESHES (GrayBoxArena.buildEnclosure) and the containment
 * COLLIDERS below — one owner, so the room the player sees and the room the
 * player can leave can never drift apart again (PLAYTEST F15).
 *
 * `gap` is the outward offset of a shell wall's center from the floor edge and
 * `thickness` its depth, so its inner face lands at floorEdge + gap −
 * thickness/2 = 0.5 outside the edge (x ±28.5 / z ∓20.5 on the 56 × 40 floor):
 * flush against, never overlapping, the 2.5 m authored `arena-wall-*` wainscot
 * curb whose own outer face is floorEdge + 0.5.
 */
export const ENCLOSURE_SHELL = { gap: .8, thickness: .6 } as const;
/**
 * Depth of the containment slabs. The shell mesh is only 0.6 thick, but a
 * HOLD_MAX_SPEED throw covers 34/60 = 0.57 m per fixed step and CCD is off on
 * trash, so a 0.6 m slab is a coin flip against a hard throw at the wall. 1.0
 * matches the authored curb's own thickness and clears the worst-case step with
 * margin; the extra 0.4 hangs OUTWARD into the void behind the shell mesh,
 * where nothing playable lives.
 */
const CONTAINMENT_THICKNESS = 1;

export interface ArenaColliderSpec {
  part: string;
  halfExtents: Vec3;
  offset: Vec3;
}

export interface ArenaBodySpec {
  pieceId: string;
  translation: Vec3;
  rotation: { x: number; y: number; z: number; w: number };
  colliders: ArenaColliderSpec[];
}

/** Surface-aligned frame shared by portal/receiver visuals and the ramp support collider. */
export function portalSurfaceOrientation(normal: THREE.Vector3): THREE.Quaternion {
  const tangent = new THREE.Vector3(0, 1, 0).cross(normal);
  if (tangent.lengthSq() < 1e-6) tangent.set(1, 0, 0); else tangent.normalize();
  const bitangent = normal.clone().cross(tangent).normalize();
  const basis = new THREE.Matrix4().makeBasis(tangent, bitangent, normal);
  return new THREE.Quaternion().setFromRotationMatrix(basis);
}

/** Pure derivation of every fixed simulation body the arena contributes, in creation order. */
export function deriveArenaPhysicsSpec(input: ArenaDefinition): ArenaBodySpec[] {
  const definition = parseArenaDefinition(input);
  const specs: ArenaBodySpec[] = [];
  for (const piece of definition.pieces) {
    if (isChutePiece(piece)) { if (piece.collidable) specs.push(chuteBody(piece)); }
    else if (piece.kind === "box" && piece.collidable) specs.push(boxBody(piece));
    else if (piece.kind === "portal-anchor" && piece.role === "ramp") specs.push(rampSupportBody(piece));
  }
  specs.push(...containmentBodies(definition));
  return specs;
}

/**
 * The perimeter containment shell (PLAYTEST F15, LOCKED-DESIGN 18.1 "enclosed
 * ceiling and walls"). NOT authored pieces: derived here from the authored
 * `arena-floor` footprint and ENCLOSURE_INTERIOR_HEIGHT so the solid room and
 * the drawn room keep a single owner, exactly like `floorExtents` derives the
 * camera box from the same two inputs.
 *
 * Why it exists: the authored `arena-wall-*` are 2.5 m wainscot curbs, but the
 * wall the player SEES is 19 m of painted steel that buildEnclosure explicitly
 * leaves non-collidable — so everything above y 2.5 around the whole perimeter
 * was open air, and the floor collider stops at the curb (|x| ≤ 28 / |z| ≤ 20)
 * with nothing beyond it. Measured on m1.4.0: a plain run-at-the-wall plus a
 * held jump puts the capsule's feet at 2.306 m, close enough that the KCC
 * catches the curb's top inner edge, calls it ground (the contact normal is
 * inside the 55° climb angle), walks the player up onto the 1.0 m crown and —
 * stick still held — straight off the far side into free fall. 23 of 48 sweep
 * trials left the world that way; the y < −10 rescue in Engine then teleported
 * them back, which is what the tenth dogfood reported as "clipped entirely
 * through a side wall". The dash also makes short walls hopeless on principle:
 * it forces vy = 0, so a 24 m/s burst off any perch costs zero altitude.
 *
 * Deliberately four walls and no ceiling: the highest reachable stance measured
 * anywhere in the arena is feet y 9.451 (a jump off `tower-roof`, top 7.1), so
 * a 19 m shell has ~9.5 m of headroom and a lid would only cost colliders. The
 * curb stays exactly as authored — it is the visible ledge, and standing on its
 * crown is fine now that there is something behind it.
 */
function containmentBodies(definition: ArenaDefinition): ArenaBodySpec[] {
  const floor = definition.pieces.find((piece) => piece.id === "arena-floor");
  if (!floor) throw new Error("Required arena piece arena-floor is missing.");
  const [width, height, depth] = floor.size as Vec3;
  // Center offset from the floor edge: the shell's inner face (gap −
  // thickness/2) plus this slab's own half-thickness, so the inner faces are
  // flush and the extra depth hangs outward.
  const inset = ENCLOSURE_SHELL.gap - ENCLOSURE_SHELL.thickness / 2 + CONTAINMENT_THICKNESS / 2;
  const half = CONTAINMENT_THICKNESS / 2;
  const halfX = width / 2 + inset;
  const halfZ = depth / 2 + inset;
  const halfY = ENCLOSURE_INTERIOR_HEIGHT / 2;
  // Base at the floor's TOP face, capped at the same ceiling the camera clamp
  // and the shell mesh use. The side slabs run the full ±halfZ so they seal the
  // four corners against the north/south slabs with no diagonal gap.
  const y = floor.position[1] + height / 2 + halfY;
  const plane = (id: string, translation: Vec3, halfExtents: Vec3): ArenaBodySpec => ({
    pieceId: id,
    translation,
    rotation: { x: 0, y: 0, z: 0, w: 1 },
    colliders: [{ part: "containment", halfExtents, offset: [0, 0, 0] }],
  });
  return [
    plane("containment-left", [floor.position[0] - halfX, y, floor.position[2]], [half, halfY, halfZ]),
    plane("containment-north", [floor.position[0], y, floor.position[2] - halfZ], [halfX, halfY, half]),
    plane("containment-right", [floor.position[0] + halfX, y, floor.position[2]], [half, halfY, halfZ]),
    plane("containment-south", [floor.position[0], y, floor.position[2] + halfZ], [halfX, halfY, half]),
  ];
}

/**
 * Pure derivation of the hazard baffle's kinematic body (LOCKED-DESIGN 3.2,
 * 18.7 rider #2). Dims and rest pose come from definition data only; the
 * runtime yaw is owned by the deterministic simulation (Simulation.baffleYawAt)
 * and applied by the engine each fixed step, never by visual code.
 */
export function deriveHazardBaffleSpec(input: ArenaDefinition): ArenaBodySpec {
  const definition = parseArenaDefinition(input);
  const piece = definition.pieces.find((candidate) => candidate.id === "hazard-baffle");
  if (!piece) throw new Error("Required arena piece hazard-baffle is missing.");
  const size = piece.size as Vec3;
  return {
    pieceId: piece.id,
    translation: [...piece.position],
    rotation: eulerRotation(piece.rotation),
    colliders: [{ part: "baffle", halfExtents: [size[0] / 2, size[1] / 2, size[2] / 2], offset: [0, 0, 0] }],
  };
}

export interface ArenaPhysicsHandles {
  /** Kinematic hazard-baffle body; the engine drives its yaw from the simulation each fixed step. */
  baffle: RAPIER.RigidBody;
}

/** Instantiates the derived specs into the live Rapier world. */
export function buildArenaPhysics(api: typeof RAPIER, world: RAPIER.World, definition: ArenaDefinition): ArenaPhysicsHandles {
  for (const spec of deriveArenaPhysicsSpec(definition)) {
    const body = world.createRigidBody(api.RigidBodyDesc.fixed().setTranslation(...spec.translation).setRotation(spec.rotation));
    for (const collider of spec.colliders) {
      world.createCollider(api.ColliderDesc.cuboid(...collider.halfExtents).setTranslation(...collider.offset), body);
    }
  }
  const baffleSpec = deriveHazardBaffleSpec(definition);
  const baffle = world.createRigidBody(api.RigidBodyDesc.kinematicPositionBased().setTranslation(...baffleSpec.translation).setRotation(baffleSpec.rotation));
  for (const collider of baffleSpec.colliders) {
    world.createCollider(api.ColliderDesc.cuboid(...collider.halfExtents).setTranslation(...collider.offset), baffle);
  }
  return { baffle };
}

function chuteBody(piece: ArenaPieceDefinition): ArenaBodySpec {
  const size = piece.size as Vec3;
  const { sideWidth, topHeight, backDepth } = CHUTE_SHELL;
  const parts: Array<{ part: string; size: Vec3; position: Vec3 }> = [
    { part: "shell-left", size: [sideWidth, size[1], size[2]], position: [-size[0] / 2 + sideWidth / 2, 0, 0] },
    { part: "shell-right", size: [sideWidth, size[1], size[2]], position: [size[0] / 2 - sideWidth / 2, 0, 0] },
    { part: "shell-top", size: [size[0], topHeight, size[2]], position: [0, size[1] / 2 - topHeight / 2, 0] },
    { part: "shell-back", size: [size[0] - sideWidth * 2, size[1] - topHeight, backDepth], position: [0, -topHeight / 2, size[2] / 2 - backDepth / 2] },
  ];
  return {
    pieceId: piece.id,
    translation: [...piece.position],
    rotation: eulerRotation(piece.rotation),
    colliders: parts.map(({ part, size: dims, position }) => ({ part, halfExtents: [dims[0] / 2, dims[1] / 2, dims[2] / 2] as Vec3, offset: position })),
  };
}

function boxBody(piece: ArenaPieceDefinition): ArenaBodySpec {
  const size = piece.size as Vec3;
  return {
    pieceId: piece.id,
    translation: [...piece.position],
    rotation: eulerRotation(piece.rotation),
    colliders: [{ part: "box", halfExtents: [size[0] / 2, size[1] / 2, size[2] / 2], offset: [0, 0, 0] }],
  };
}

// The ramp support slab has always been solid regardless of the anchor's
// collidable flag; parity with the historical builder requires keeping that.
function rampSupportBody(piece: ArenaPieceDefinition): ArenaBodySpec {
  const size = piece.size as [number, number];
  const normal = new THREE.Vector3().fromArray(piece.normal!).normalize();
  const orientation = portalSurfaceOrientation(normal);
  const center = new THREE.Vector3().fromArray(piece.position).addScaledVector(normal, RAMP_SUPPORT.normalOffset);
  return {
    pieceId: piece.id,
    translation: [center.x, center.y, center.z],
    rotation: { x: orientation.x, y: orientation.y, z: orientation.z, w: orientation.w },
    colliders: [{ part: "ramp-support", halfExtents: [(size[0] + RAMP_SUPPORT.margin) / 2, (size[1] + RAMP_SUPPORT.margin) / 2, RAMP_SUPPORT.depth / 2], offset: [0, 0, 0] }],
  };
}

function eulerRotation(rotation: Vec3): ArenaBodySpec["rotation"] {
  const quaternion = new THREE.Quaternion().setFromEuler(new THREE.Euler(...rotation));
  return { x: quaternion.x, y: quaternion.y, z: quaternion.z, w: quaternion.w };
}
